<template>
  <main class="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
    <!-- En-tête - SEULEMENT POUR LE DASHBOARD -->
    <div v-if="activeMenu === 'dashboard'" class="mb-6">
      <h2 class="text-2xl font-extrabold text-blue-900 dark:text-gray-100">{{ pageTitle }}</h2>
      <p class="text-gray-600 dark:text-gray-400 mt-1">{{ pageDescription }}</p>
    </div>
    
    <!-- Sous-menu pour Rôles & Permissions -->
    <div v-if="activeMenu === 'roles'" class="mb-6">
      <div class="flex space-x-4 border-b border-gray-200 dark:border-gray-700">
        <button
          @click="activeSubMenu = 'permissions'"
          :class="[
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            activeSubMenu === 'permissions'
              ? 'border-blue-900 text-blue-900 dark:border-blue-300 dark:text-blue-300'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          ]"
        >
          Gestion des Permissions
        </button>
        <button
          @click="activeSubMenu = 'roles'"
          :class="[
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            activeSubMenu === 'roles'
              ? 'border-blue-900 text-blue-900 dark:border-blue-300 dark:text-blue-300'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          ]"
        >
          Gestion des Rôles
        </button>
      </div>
    </div>
    
    <!-- Contenu dynamique -->
    <div v-if="activeMenu === 'stats'">
      <StatisticsContent
        :user-roles="userRoles"
        :user-permissions="userPermissions"
        :key="'stats-' + statsKey"
      />
    </div>
    <component 
      v-else
      :is="activeComponent" 
      :search-query="searchQuery"
      :user-roles="userRoles"
      :user-permissions="userPermissions"
      :user="currentUser"
      :selected-ticket-code="selectedTicketCode"
      @update-search="handleUpdateSearch"
      @profile-updated="handleProfileUpdated"
      @clear-ticket-filter="handleClearTicketFilter"
    />
  </main>
</template>

<script>
import UsersContent from './UsersContent.vue';
import MotifsContent from './MotifsContent.vue';
import GammesContent from './GammesContent.vue';
import ProduitsContent from './ProduitsContent.vue';
import TicketsContent from './TicketsContent.vue';
import PermissionsContent from './PermissionContent.vue';
import RoleContent from './RoleContent.vue';
import StatisticsContent from './StatistiqueContent.vue';
import ProfileContent from './ProfileContent.vue';

// Import Chart.js
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

// Composant Dashboard Home avec les cadres et graphiques
const DashboardHome = {
  name: 'DashboardHome',
  template: `
    <div class="space-y-6">
      <!-- Statistiques principales - 5 CADRES PAR LIGNE -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <!-- Utilisateurs - Visible seulement pour les admins -->
        <div v-if="isAdmin" class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center">
            <div class="p-2 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 mr-3">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <div>
              <p class="text-xs font-medium text-gray-600 dark:text-gray-400">Utilisateurs</p>
              <p class="text-xl font-bold text-blue-900 dark:text-blue-300">{{ stats.totalUsers }}</p>
            </div>
          </div>
        </div>
        
        <!-- Tickets totaux - Visible pour tous -->
        <div v-if="isAdmin" class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center">
            <div class="p-2 rounded-full bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 mr-3">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"></path>
              </svg>
            </div>
            <div>
              <p class="text-xs font-medium text-gray-600 dark:text-gray-400">Tickets totaux</p>
              <p class="text-xl font-bold text-blue-900 dark:text-blue-300">{{ isAdmin ? stats.totalTickets : stats.mesTickets }}</p>
            </div>
          </div>
        </div>
        
        <!-- Gammes - Visible seulement pour les admins et responsables -->
        <div v-if="isAdmin" class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center">
            <div class="p-2 rounded-full bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 mr-3">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
              </svg>
            </div>
            <div>
              <p class="text-xs font-medium text-gray-600 dark:text-gray-400">Gammes</p>
              <p class="text-xl font-bold text-blue-900 dark:text-blue-300">{{ isAdmin ? stats.totalGammes : stats.mesGammes }}</p>
            </div>
          </div>
        </div>
        
        <!-- Motifs - Visible seulement pour les admins et responsables -->
        <div v-if="isAdmin" class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center">
            <div class="p-2 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 mr-3">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"></path>
              </svg>
            </div>
            <div>
              <p class="text-xs font-medium text-gray-600 dark:text-gray-400">Motifs</p>
              <p class="text-xl font-bold text-blue-900 dark:text-blue-300">{{ isAdmin ? stats.totalMotifs : stats.mesMotifs }}</p>
            </div>
          </div>
        </div>
        
        <!-- Produits - Visible seulement pour les admins et responsables -->
        <div v-if="isAdmin" class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center">
            <div class="p-2 rounded-full bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 mr-3">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
              </svg>
            </div>
            <div>
              <p class="text-xs font-medium text-gray-600 dark:text-gray-400">Produits</p>
              <p class="text-xl font-bold text-blue-900 dark:text-blue-300">{{ isAdmin ? stats.totalProduits : stats.mesProduits }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Statistiques des tickets par statut - 4 CADRES PAR LIGNE -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Tickets Ouverts -->
        <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center">
            <div class="p-2 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 mr-3">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
            </div>
            <div>
              <p class="text-xs font-medium text-gray-600 dark:text-gray-400">Tickets Ouverts</p>
              <p class="text-xl font-bold text-blue-900 dark:text-blue-300">{{ stats.ticketsOuverts }}</p>
            </div>
          </div>
        </div>
        
        <!-- Tickets En attente -->
        <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center">
            <div class="p-2 rounded-full bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 mr-3">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div>
              <p class="text-xs font-medium text-gray-600 dark:text-gray-400">Tickets En attente</p>
              <p class="text-xl font-bold text-blue-900 dark:text-blue-300">{{ stats.ticketsEnAttente }}</p>
            </div>
          </div>
        </div>
        
        <!-- Tickets Résolus -->
        <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center">
            <div class="p-2 rounded-full bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 mr-3">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div>
              <p class="text-xs font-medium text-gray-600 dark:text-gray-400">Tickets Résolus</p>
              <p class="text-xl font-bold text-blue-900 dark:text-blue-300">{{ stats.ticketsResolus }}</p>
            </div>
          </div>
        </div>
        
        <!-- Tickets Fermés -->
        <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center">
            <div class="p-2 rounded-full bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 mr-3">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <div>
              <p class="text-xs font-medium text-gray-600 dark:text-gray-400">Tickets Fermés</p>
              <p class="text-xl font-bold text-blue-900 dark:text-blue-300">{{ stats.ticketsFermes }}</p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Graphiques principaux -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Graphique Gamme vs Statut -->
        <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div class="flex justify-between items-center mb-6">
            <h3 class="text-lg font-semibold text-blue-900 dark:text-blue-300">Tickets par Gamme et Statut</h3>
          </div>
          <div class="h-80">
            <canvas ref="gammeStatusChart"></canvas>
          </div>
        </div>
        
        <!-- Graphique Gamme vs Motif -->
        <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div class="flex justify-between items-center mb-6">
            <h3 class="text-lg font-semibold text-blue-900 dark:text-blue-300">Tickets par Gamme et Motif</h3>
          </div>
          <div class="h-80">
            <canvas ref="gammeMotifChart"></canvas>
          </div>
        </div>
        
        <!-- Graphique Mensuel -->
        <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div class="flex justify-between items-center mb-6">
            <h3 class="text-lg font-semibold text-blue-900 dark:text-blue-300">Tickets par Mois</h3>
          </div>
          <div class="h-80">
            <canvas ref="monthlyChart"></canvas>
          </div>
        </div>
        
        <!-- Graphique Wilaya -->
        <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div class="flex justify-between items-center mb-6">
            <h3 class="text-lg font-semibold text-blue-900 dark:text-blue-300">Tickets par Wilaya</h3>
          </div>
          <div class="h-80">
            <canvas ref="wilayaChart"></canvas>
          </div>
        </div>
      </div>

      <!-- Métriques de performance -->
      <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <h3 class="text-xl font-semibold text-blue-900 dark:text-blue-300 mb-6">Performance du support client</h3>
        
        <!-- Cadres des taux -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <!-- Temps de réponse moyen -->
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-4 shadow-sm">
            <div class="flex items-center">
              <div class="p-2 rounded-full bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 mr-3">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Temps réponse moyen</p>
                <p class="text-xl font-bold text-blue-900 dark:text-blue-300">{{ stats.tempsReponseMoyen }}h</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Première réponse</p>
              </div>
            </div>
          </div>
          
          <!-- Temps de fermeture moyen -->
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-4 shadow-sm">
            <div class="flex items-center">
              <div class="p-2 rounded-full bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 mr-3">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Temps fermeture moyen</p>
                <p class="text-xl font-bold text-blue-900 dark:text-blue-300">{{ stats.tempsFermetureMoyen }}h</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">De l'ouverture à la fermeture</p>
              </div>
            </div>
          </div>
          
          <!-- Taux de réponse -->
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-4 shadow-sm">
            <div class="flex items-center">
              <div class="p-2 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 mr-3">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                </svg>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Taux de réponse</p>
                <p class="text-xl font-bold text-blue-900 dark:text-blue-300">{{ stats.tauxReponse }}%</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Tickets avec réponse</p>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Graphiques des taux -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Graphique Évolution des Temps de Traitement -->
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-4 shadow-sm">
            <div class="flex justify-between items-center mb-4">
              <h4 class="text-md font-semibold text-blue-900 dark:text-blue-300">Évolution des Temps de Traitement</h4>
            </div>
            <div class="h-64">
              <canvas ref="traitementTimeChart"></canvas>
            </div>
          </div>
          
          <!-- Graphique Taux de Réponse Mensuel -->
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-4 shadow-sm">
            <div class="flex justify-between items-center mb-4">
              <h4 class="text-md font-semibold text-blue-900 dark:text-blue-300">Taux de Réponse Mensuel</h4>
            </div>
            <div class="h-64">
              <canvas ref="tauxReponseChart"></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  props: {
    userRoles: {
      type: Array,
      default: () => []
    },
    userPermissions: {
      type: Array,
      default: () => []
    },
    selectedTicketCode: {
      type: String,
      default: ''
    }
  },
  computed: {
    isAdmin() {
      const roles = this.userRoles || [];
      return roles.includes('Administrateur') || 
             roles.includes('administrateur') || 
             roles.includes('admin') ||
             roles.includes('Administration');
    },
    isResponsable() {
      const roles = this.userRoles || [];
      return roles.includes('Responsable') || 
             roles.includes('responsable');
    }
  },
  data() {
    return {
      stats: {
        totalUsers: 0,
        totalTickets: 0,
        totalGammes: 0,
        totalProduits: 0,
        totalMotifs: 0,
        ticketsOuverts: 0,
        ticketsEnAttente: 0,
        ticketsResolus: 0,
        ticketsFermes: 0,
        mesTickets: 0,
        mesGammes: 0,
        mesMotifs: 0,
        mesProduits: 0,
        tempsReponseMoyen: 0,
        tempsFermetureMoyen: 0,
        tauxReponse: 0
      },
      chartInstances: {}
    }
  },
  async mounted() {
    await this.loadStats();
    await this.loadCharts();
  },
  beforeUnmount() {
    // Détruire tous les graphiques
    Object.values(this.chartInstances).forEach(chart => {
      if (chart) chart.destroy();
    });
  },
  methods: {
    async loadStats() {
      try {
        const response = await fetch('/api/dashboard/stats');
        const data = await response.json();
        
        if (data.success) {
          this.stats = { ...this.stats, ...data.stats };
        }
      } catch (error) {
        console.error('Erreur chargement stats:', error);
      }
    },

    async loadCharts() {
      try {
        const response = await fetch('/api/dashboard/statistics');
        const data = await response.json();
        
        if (data.success) {
          this.renderCharts(data.data);
        } else {
          this.showChartError('Erreur lors du chargement des données');
        }
      } catch (error) {
        this.showChartError('Erreur de connexion au serveur');
      }
    },

    showChartError(message) {
      const chartRefs = ['traitementTimeChart', 'tauxReponseChart', 'gammeStatusChart', 'gammeMotifChart', 'monthlyChart', 'wilayaChart'];
      
      chartRefs.forEach(ref => {
        if (this.$refs[ref]) {
          this.renderEmptyChart(ref, message);
        }
      });
    },

    renderCharts(chartData) {
      // Graphique Gamme vs Statut
      if (chartData.gamme_status && chartData.gamme_status.labels && chartData.gamme_status.labels.length > 0) {
        this.renderBarChart('gammeStatusChart', chartData.gamme_status, 'bar');
      } else {
        this.renderEmptyChart('gammeStatusChart', 'Aucune donnée disponible');
      }
      
      // Graphique Gamme vs Motif
      if (chartData.gamme_motif && chartData.gamme_motif.labels && chartData.gamme_motif.labels.length > 0) {
        this.renderBarChart('gammeMotifChart', chartData.gamme_motif, 'bar');
      } else {
        this.renderEmptyChart('gammeMotifChart', 'Aucune donnée disponible');
      }
      
      // Graphique Mensuel
      if (chartData.monthly && chartData.monthly.labels && chartData.monthly.labels.length > 0) {
        this.renderLineChart('monthlyChart', chartData.monthly);
      } else {
        this.renderEmptyChart('monthlyChart', 'Aucune donnée disponible');
      }
      
      // Graphique Wilaya
      if (chartData.wilaya && chartData.wilaya.labels && chartData.wilaya.labels.length > 0) {
        this.renderDoughnutChart('wilayaChart', chartData.wilaya);
      } else {
        this.renderEmptyChart('wilayaChart', 'Aucune donnée disponible');
      }
      
      // Graphique Évolution des Temps de Traitement
      if (chartData.traitement_times && chartData.traitement_times.labels && chartData.traitement_times.labels.length > 0) {
        this.renderTraitementTimeChart('traitementTimeChart', chartData.traitement_times);
      } else {
        this.renderEmptyChart('traitementTimeChart', 'Aucune donnée de temps de traitement disponible');
      }
      
      // Graphique Taux de Réponse Mensuel
      if (chartData.taux_reponse && chartData.taux_reponse.labels && chartData.taux_reponse.labels.length > 0) {
        this.renderTauxReponseChart('tauxReponseChart', chartData.taux_reponse);
      } else {
        this.renderEmptyChart('tauxReponseChart', 'Aucune donnée de taux de réponse disponible');
      }
    },

    renderBarChart(ref, data, type = 'bar') {
      if (!this.$refs[ref]) return;
      
      const ctx = this.$refs[ref].getContext('2d');
      
      if (this.chartInstances[ref]) {
        this.chartInstances[ref].destroy();
      }

      this.chartInstances[ref] = new Chart(ctx, {
        type: type,
        data: data,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  return `${context.dataset.label}: ${context.parsed.y} tickets`;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Nombre de tickets'
              }
            },
            x: {
              title: {
                display: true,
                text: 'Gammes'
              }
            }
          }
        }
      });
    },

    renderLineChart(ref, data) {
      if (!this.$refs[ref]) return;
      
      const ctx = this.$refs[ref].getContext('2d');
      
      if (this.chartInstances[ref]) {
        this.chartInstances[ref].destroy();
      }

      this.chartInstances[ref] = new Chart(ctx, {
        type: 'line',
        data: data,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  return `Tickets: ${context.parsed.y}`;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Nombre de tickets'
              }
            },
            x: {
              title: {
                display: true,
                text: 'Mois'
              }
            }
          }
        }
      });
    },

    renderDoughnutChart(ref, data) {
      if (!this.$refs[ref]) return;
      
      const ctx = this.$refs[ref].getContext('2d');
      
      if (this.chartInstances[ref]) {
        this.chartInstances[ref].destroy();
      }

      this.chartInstances[ref] = new Chart(ctx, {
        type: 'doughnut',
        data: data,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'right',
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const label = context.label || '';
                  const value = context.parsed;
                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                  return `${label}: ${value} tickets (${percentage}%)`;
                }
              }
            }
          }
        }
      });
    },

    renderTraitementTimeChart(ref, data) {
      if (!this.$refs[ref]) return;
      
      const ctx = this.$refs[ref].getContext('2d');
      
      if (this.chartInstances[ref]) {
        this.chartInstances[ref].destroy();
      }

      if (!data || !data.labels || data.labels.length === 0) {
        this.renderEmptyChart(ref, 'Aucune donnée de temps de traitement disponible');
        return;
      }

      this.chartInstances[ref] = new Chart(ctx, {
        type: 'line',
        data: data,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  return `${context.dataset.label}: ${context.parsed.y}h`;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Temps (heures)'
              }
            },
            x: {
              title: {
                display: true,
                text: 'Mois'
              }
            }
          }
        }
      });
    },

    renderTauxReponseChart(ref, data) {
      if (!this.$refs[ref]) return;
      
      const ctx = this.$refs[ref].getContext('2d');
      
      if (this.chartInstances[ref]) {
        this.chartInstances[ref].destroy();
      }

      if (!data || !data.labels || data.labels.length === 0) {
        this.renderEmptyChart(ref, 'Aucune donnée de taux de réponse disponible');
        return;
      }

      this.chartInstances[ref] = new Chart(ctx, {
        type: 'bar',
        data: data,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  return `${context.dataset.label}: ${context.parsed.y}%`;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              max: 100,
              title: {
                display: true,
                text: 'Taux de réponse (%)'
              }
            },
            x: {
              title: {
                display: true,
                text: 'Mois'
              }
            }
          }
        }
      });
    },

    renderEmptyChart(ref, message) {
      const ctx = this.$refs[ref].getContext('2d');
      ctx.clearRect(0, 0, this.$refs[ref].width, this.$refs[ref].height);
      ctx.fillStyle = '#6B7280';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(message, this.$refs[ref].width / 2, this.$refs[ref].height / 2);
    }
  }
};

export default {
  name: 'DashboardContent',
  components: {
    UsersContent,
    MotifsContent,
    GammesContent,
    ProduitsContent,
    TicketsContent,
    PermissionsContent,
    RoleContent,
    StatisticsContent,
    ProfileContent
  },
  data() {
    return {
      activeSubMenu: 'permissions',
      currentUser: {},
      statsKey: 0
    };
  },
  props: {
    activeMenu: String,
    userRoles: {
      type: Array,
      default: () => []
    },
    userPermissions: {
      type: Array,
      default: () => []
    },
    userName: {
      type: String,
      default: ''
    },
    searchQuery: {
      type: String,
      default: ''
    },
    userData: {
      type: Object,
      default: () => ({})
    },
    selectedTicketCode: {
      type: String,
      default: ''
    }
  },
  computed: {
    pageTitle() {
      const titles = {
        dashboard: 'Tableau de bord',
        users: 'Gestion des Utilisateurs',
        tickets: 'Gestion des Tickets',
        roles: 'Rôles & Permissions',
        stats: 'Statistiques & Graphiques',
        profile: 'Mon Profil',
        produits: 'Gestion des Produits'
      };
      return titles[this.activeMenu] || 'Tableau de bord';
    },
    pageDescription() {
      const descriptions = {
        users: 'Gérez les utilisateurs et leurs permissions',
        tickets: 'Suivez et gérez les tickets de support',
        roles: 'Configurez les rôles et permissions système',
        stats: 'Analyses détaillées et rapports de performance',
        profile: 'Gérez vos informations personnelles',
        produits: 'Gérez les produits et leurs gammes associées'
      };
      return descriptions[this.activeMenu] || 'Vue d\'ensemble de votre activité';
    },
    activeComponent() {
      const components = {
        dashboard: DashboardHome,
        users: UsersContent,
        categories: GammesContent,
        produits: ProduitsContent,
        motifs: MotifsContent,
        tickets: TicketsContent,
        roles: this.activeSubMenu === 'permissions' ? PermissionsContent : RoleContent,
        profile: ProfileContent
      };
      return components[this.activeMenu] || DashboardHome;
    }
  },
  mounted() {
    this.initializeCurrentUser(); 
  },
  watch: {
    userData: {
      handler(newUserData) {
        if (newUserData && Object.keys(newUserData).length > 0) {
          this.currentUser = { ...newUserData };
        }
      },
      immediate: true,
      deep: true
    },
    activeMenu(newMenu) {
      if (newMenu === 'profile') {
        this.initializeCurrentUser();
      }
      
      if (newMenu === 'stats') {
        this.refreshStatistics();
      }
    },
    selectedTicketCode(newCode, oldCode) {
      // Logique existante pour les tickets
    }
  },
  methods: {
    initializeCurrentUser() {
      if (this.userData && Object.keys(this.userData).length > 0) {
        this.currentUser = { ...this.userData };
      } else {
        this.currentUser = {
          name: this.userName,
          email: 'utilisateur@example.com',
          roles: this.userRoles.map(role => ({ name: role })),
          permissions: this.userPermissions,
          created_at: new Date().toISOString(),
          last_login_at: null
        };
      }
    },
    
    handleUpdateSearch(query) {
      this.$emit('update-search', query);
    },
    
    handleProfileUpdated(updatedUser) {
      this.currentUser = { ...this.currentUser, ...updatedUser };
      this.$emit('profile-updated', updatedUser);
    },
    
    handleTicketSelected(ticket) {
      this.$emit('ticket-selected', ticket);
    },
    
    handleClearTicketFilter() {
      this.$emit('clear-ticket-filter');
    },
    
    refreshStatistics() {
      this.statsKey += 1;
    }
  }
};
</script>